import type {
  BoggleAchievementDefinition,
  BoggleAchievementTierDefinition,
  BoggleAchievementVisibility,
} from '~/boggle/config/achievements/types'
import type {
  BoggleBadgeProgressProjection,
  PlayerAchievementState,
} from '~/boggle/progress/types'
import type { BoggleAchievementCareerStats } from './careerStats'
import {
  getBoggleAchievementRequirementProgress,
  type BoggleAchievementEvaluationContext,
  type BoggleAchievementTierProgress,
} from './evaluateAchievements'

export type BoggleKnownAchievementTier = {
  completed: boolean
  definition: BoggleAchievementTierDefinition
  progress: BoggleAchievementTierProgress
}

export type BoggleKnownAchievement = {
  completedTierCount: number
  definition: BoggleAchievementDefinition
  tiers: readonly BoggleKnownAchievementTier[]
  totalTierCount: number
}

export type BoggleKnownAchievementsView = {
  achievements: readonly BoggleKnownAchievement[]
  completedTierCount: number
  totalTierCount: number
}

function isKnownByVisibility({
  state,
  tier,
  visibility,
}: {
  state: PlayerAchievementState
  tier?: BoggleAchievementTierDefinition
  visibility: BoggleAchievementVisibility
}) {
  if (visibility === 'visible' || visibility === 'veiled') {
    return true
  }

  if (tier && state.unlockedTierIds.includes(tier.id)) {
    return true
  }

  if (visibility === 'hidden_until_prerequisite') {
    return tier?.revealRatingId
      ? state.revealedRatingIds.includes(tier.revealRatingId)
      : false
  }

  return false
}

export function getKnownAchievements(
  state: PlayerAchievementState,
  definitions: readonly BoggleAchievementDefinition[],
  careerStats: BoggleAchievementCareerStats,
  badgeProgress: BoggleBadgeProgressProjection,
): BoggleKnownAchievementsView {
  const context: BoggleAchievementEvaluationContext = { badgeProgress, careerStats }
  const unlockedTierIds = new Set(state.unlockedTierIds)

  const achievements = definitions.flatMap((definition): BoggleKnownAchievement[] => {
    const knownDefinition = isKnownByVisibility({
      state,
      visibility: definition.visibility,
    })
    const tiers = definition.tiers.flatMap((tier): BoggleKnownAchievementTier[] => {
      const tierVisibility = tier.visibility ?? definition.visibility
      if (
        !knownDefinition &&
        !isKnownByVisibility({ state, tier, visibility: tierVisibility })
      ) {
        return []
      }
      if (!isKnownByVisibility({ state, tier, visibility: tierVisibility })) {
        return []
      }

      const progress = getBoggleAchievementRequirementProgress(tier.requirement, context)
      return [
        {
          completed: unlockedTierIds.has(tier.id) || progress.completed,
          definition: tier,
          progress,
        },
      ]
    })

    if (tiers.length === 0) {
      return []
    }

    return [
      {
        completedTierCount: tiers.filter(tier => tier.completed).length,
        definition,
        tiers,
        totalTierCount: tiers.length,
      },
    ]
  })

  return {
    achievements,
    completedTierCount: achievements.reduce(
      (sum, achievement) => sum + achievement.completedTierCount,
      0,
    ),
    totalTierCount: achievements.reduce(
      (sum, achievement) => sum + achievement.totalTierCount,
      0,
    ),
  }
}
